<template>
  <div style="width: 100%">
    <div class="readiness-container">
      <AverageReadinessBanner
        :averageReadiness="employeesAverageReadiness"
        :last_month_averageReadiness="lastMonthAverageReadiness"
        averageReadinessTitle="Employees Average Readiness"
      />
      <EmployeeReadinessMenu
        :items="menuItems"
        @onChange="onChangeMenu"
        class="employee-readiness-menu-container"
      />
    </div>
    <v-tabs-items v-model="selectedTab" class="custom-tabs">
      <v-tab-item v-for="(item, index) in menuItems" :key="index">
        <v-card elevation="0" class="employee-readiness-container">
          <template v-if="item.title === 'Employee Directory' && showEmployeeDirectoryInline">
            <EmployeeDirectoryPage
              :key="employeeDirectoryInlineKey"
              class="directories-view__employee-directory-page"
              :header="employeeDirectoryPreviewHeader"
              :current-employee="directorySelectedEmployee"
              :path-tab="employeeDirectoryPathTab"
              :badges-tab="employeeDirectoryBadgesTab"
              :initial-tab="employeeDirectoryInlineInitialTab"
              @breadcrumb-click="onEmployeeDirectoryBreadcrumb"
            />
          </template>
          <template v-else-if="item.title === 'Employee Directory'">
            <EmployeesDirectoryTable
              :dataSource="dataSource"
              :backendPagination="true"
              :itemsCount="employeeTotalCount"
              :currentPage="employeePage"
              :itemsPerPage="employeeLimit"
              :searchText="employeeSearchText"
              :loading="employeesInitialLoading"
              @onRequest="onEmployeePageRequest"
              @onSearch="onEmployeeSearch"
            />
          </template>

          <template
            v-else-if="
              item.title === 'Responsibilities Directory' && showResponsibilityDirectoryInline
            "
          >
            <ResponsibilityDirectoryPage
              :key="responsibilityDirectoryInlineKey"
              class="directories-view__responsibility-directory-page"
              v-bind="responsibilityDirectoryPageProps"
              @breadcrumb-click="onResponsibilityDirectoryBreadcrumb"
              @tab-change="onResponsibilityDirectoryTabChange"
              :res-id="directorySelectedResponsibilityId"
            >
              <template #context>
                <div class="directories-view__context-purpose">
                  <p
                    v-if="responsibilityDrilldownPurposeTrimmed"
                    class="directories-view__context-purpose-text"
                  >
                    {{ responsibilityDrilldownPurposeTrimmed }}
                  </p>
                  <p v-else class="directories-view__context-purpose-empty">
                    No purpose has been added for this responsibility yet.
                  </p>
                </div>
              </template>
            </ResponsibilityDirectoryPage>
          </template>
          <template v-else-if="item.title === 'Responsibilities Directory'">
            <ResponsibilitiesDirectoryTable
              :dataSource="respDataSource"
              :backendPagination="true"
              :itemsCount="respTotalCount"
              :currentPage="respPage"
              :itemsPerPage="respLimit"
              :searchText="respSearchText"
              :loading="respSkeletonLoading"
              @onRequest="onRespPageRequest"
              @onSearch="onRespSearch"
            />
          </template>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import AverageReadinessBanner from "@components/AverageReadinessBanner/AverageReadinessBanner.vue";
import {
  getBadgeViewFromCause,
  getUpgradedVariantIconFromCause,
} from "@components/utils/proficiencyBadgeView";
import EmployeeReadinessMenu from "@components/EmployeeReadinessMenu/EmployeeReadinessMenu.vue";
import EmployeesDirectoryTable from "@components/EmployeesDirectoryTable/EmployeesDirectoryTable.vue";
import ResponsibilitiesDirectoryTable from "@components/ResponsibilitiesDirectoryTable/ResponsibilitiesDirectoryTable.vue";
import EmployeeDirectoryPage from "@components/EmployeeDirectoryPage/EmployeeDirectoryPage.vue";
import { useEmployeesDirectoryTableViewState } from "@components/EmployeesDirectoryTable/composables/useViewState";
import ResponsibilityDirectoryPage from "@components/ResponsibilityDirectoryPage/ResponsibilityDirectoryPage.vue";
import {
  getDirectoryEmployeesV2,
  getDirectoryResponsibilitiesV2,
  getAccountData,
  getResponsibilityChecklistV2,
  getListofResponsibilityBadge,
  getResponsibilityIdV2,
} from "@/api";
import { LD_FLAG_IDEA_MDP_560 } from "@components/constants/launchDarklyFlagKeys";

export default {
  components: {
    AverageReadinessBanner,
    EmployeeReadinessMenu,
    EmployeesDirectoryTable,
    ResponsibilitiesDirectoryTable,
    EmployeeDirectoryPage,
    ResponsibilityDirectoryPage,
  },
  setup() {
    /**
     * Initialize the shared directory-view singleton inside setup() so the
     * first call always happens in a valid Vue current-instance context.
     * Previously this was invoked from a computed getter, which crashed on
     * first render of /your-stuff/directories with
     * "Cannot read properties of null (reading 'proxy')" because Options-API
     * computed getters do not run inside a setup context.
     */
    const directoryView = useEmployeesDirectoryTableViewState();
    return { directoryView };
  },
  data() {
    return {
      dataSource: [],
      respDataSource: [],
      // ER-10603 — true only until the first employee fetch settles; refreshes never re-arm it.
      employeesInitialLoading: true,
      accountID: null,
      authUser: null,
      topLevel: "position",
      removeBorder: false,
      selectedTab: 0,
      currentAccountDetails: {},
      // Employee directory pagination (active only from backend)
      employeePage: 1,
      employeeLimit: 20,
      employeeTotalCount: 0,
      activeEmployeeCount: 0,
      employeeSearchText: "",
      // Responsibility directory pagination (active only from backend)
      respPage: 1,
      respLimit: 10,
      respTotalCount: 0,
      activeResponsibilityCount: 0,
      respSearchText: "",
      respLoading: false,
      skeletonLoadingEnabled: false,
      responsibilityDrilldownChecklistTasks: [],
      responsibilityDrilldownBadgeHolders: [],
      responsibilityDrilldownBadgeHoldersCount: 0,
      responsibilityDrilldownPurpose: "",
      previewEmployeeDirectoryBadgesRows: [
        {
          id: 1,
          currentBadge: {
            level: "Apprentice",
            dot: "Good Standing",
            view: "Editable",
            expiration_date: null,
            has_expiration_if_obsolete: false,
            is_current: true,
          },
          responsibility: { code: "#9999", name: "Responsibility Name" },
          issued_by: {
            name: "Olivia Rhye",
            picture: "https://cdn.vuetifyjs.com/images/john.jpg",
          },
          issued_on: "2024-01-01 12:45",
        },
        {
          id: 2,
          currentBadge: {
            level: "Professional",
            dot: "Good Standing",
            view: "Editable",
            expiration_date: null,
            has_expiration_if_obsolete: false,
            is_current: true,
          },
          responsibility: { code: "#9999", name: "Responsibility Name" },
          issued_by: {
            name: "Olivia Rhye",
            picture: "https://cdn.vuetifyjs.com/images/john.jpg",
          },
          issued_on: "2024-01-01 12:45",
        },
      ],
    };
  },
  created() {
    this.accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    this.currentAccountDetails = this.getInitialAccountDetails();
  },
  async mounted() {
    this.directoryView.setEmbedded(true);
    try {
      await this.initSkeletonLoadingFlag();
      await this.getAccountDetails();
      if (this.menuItems.length > 0) {
        const showEmployee = this.currentAccountDetails?.show_employee_directory !== false;
        const showResponsibilities =
          this.currentAccountDetails?.show_responsibilities_directories !== false;
        if (showEmployee && showResponsibilities) {
          await Promise.all([this.getDirectoryEmployees(), this.getDirectoryResponsibilities()]);
        } else if (showEmployee) {
          await this.getDirectoryEmployees();
        } else if (showResponsibilities) {
          await this.getDirectoryResponsibilities();
        }
      }
    } finally {
      this.employeesInitialLoading = false;
    }
  },
  beforeUnmount() {
    this.directoryView.resetInlineDirectoryView();
    this.directoryView.setEmbedded(false);
  },
  computed: {
    respSkeletonLoading() {
      return this.respLoading && this.skeletonLoadingEnabled;
    },
    showEmployeeDirectoryInline() {
      const s = this.directoryView.state;
      return s.embedded && (s.showEmployeeBadgesList || s.showEmployeeReadinessPath);
    },
    showResponsibilityDirectoryInline() {
      const s = this.directoryView.state;
      return s.embedded && s.showResponsibilityDirectoryCard;
    },
    directorySelectedResponsibilityId() {
      return this.directoryView.state.selectedResponsibilityId ?? null;
    },
    responsibilityDirectoryInlineKey() {
      if (!this.showResponsibilityDirectoryInline) return "closed";
      return String(this.directorySelectedResponsibilityId ?? "none");
    },
    employeeDirectoryInlineInitialTab() {
      const s = this.directoryView.state;
      if (s.showEmployeeBadgesList) return "Badges";
      if (s.showEmployeeReadinessPath) return "Path to Readiness";
      return "Path to Readiness";
    },
    directoryInlineEmployeeId() {
      return this.directoryView.state.selectedEmployee?.id ?? null;
    },
    directorySelectedEmployee() {
      return this.directoryView.state.selectedEmployee || null;
    },
    employeeDirectoryInlineKey() {
      if (!this.showEmployeeDirectoryInline) return "closed";
      return String(this.directoryInlineEmployeeId ?? "none");
    },
    responsibilityDirectoryPreviewHeader() {
      const listHref =
        this.$route.name === "YourStuffDirectories" ? "/your-stuff/directories" : "/directories";
      return {
        breadcrumbsList: [
          { text: "Responsibilities Directory", href: listHref },
          { text: "Responsibility", disabled: true },
        ],
      };
    },
    responsibilityDirectoryInlineInitialTab() {
      const tab = this.$route.query.resTab;
      if (tab === "checklist") return 1; // TAB_CHECKLIST
      if (tab === "badge-holders") return 2; // TAB_BADGE_HOLDERS
      if (tab === "assignments") return 0; // TAB_ASSIGNMENTS
      return 0; // TAB_ASSIGNMENTS
    },
    employeeDirectoryPreviewHeader() {
      const listHref =
        this.$route.name === "YourStuffDirectories" ? "/your-stuff/directories" : "/directories";
      let avatar = "";
      try {
        avatar = require("@components/assets/avatar.png");
      } catch {
        avatar = "";
      }
      return {
        firstName: "Olivia",
        lastName: "Rhye",
        avatar,
        role: "HR Manager",
        status: "active",
        access: "has-access",
        readiness: 100,
        lastMonthReadiness: 70,
        breadcrumbsList: [
          { text: "Employees Directory", href: listHref },
          { text: "Employee", disabled: true },
        ],
      };
    },
    employeeDirectoryBadgesTab() {
      if (!this.showEmployeeDirectoryInline || !this.directoryInlineEmployeeId) {
        return {
          dataStream: this.previewEmployeeDirectoryBadgesRows,
        };
      }
      return {
        accountId: this.accountID,
        employeeId: this.directoryInlineEmployeeId,
        dataStream: [],
      };
    },
    employeeDirectoryPathTab() {
      if (!this.showEmployeeDirectoryInline || !this.directoryInlineEmployeeId) {
        return {};
      }
      return {
        useDefaultDemoData: false,
        accountId: this.accountID,
        employeeId: this.directoryInlineEmployeeId,
        currentEmployee: this.directorySelectedEmployee,
        topLevel: "position",
      };
    },
    menuItems() {
      const items = [];
      const showEmployee = this.currentAccountDetails?.show_employee_directory !== false;
      const showResponsibilities =
        this.currentAccountDetails?.show_responsibilities_directories !== false;

      if (showEmployee) {
        items.push({
          title: "Employee Directory",
          count: this.activeEmployeeCount ?? 0,
        });
      }
      if (showResponsibilities) {
        items.push({
          title: "Responsibilities Directory",
          count: this.activeResponsibilityCount ?? 0,
        });
      }
      if (items.length === 0) {
        items.push({
          title: "Employee Directory",
          count: 0,
        });
        items.push({
          title: "Responsibilities Directory",
          count: 0,
        });
      }
      return items;
    },
    employeesAverageReadiness() {
      const active = this.dataSource?.filter(
        (el) => el?.current_status === "active" && el?.current_readiness !== null
      );
      if (!active?.length) return 0;
      const sum = active.reduce((acc, el) => acc + Number(el.current_readiness), 0);
      return Math.round(sum / active.length);
    },
    lastMonthAverageReadiness() {
      return null;
    },
    responsibilityDirectorySnapshot() {
      return this.directoryView.state.selectedResponsibilityDirectoryRow || null;
    },
    responsibilityDirectoryAttachedRoles() {
      const roles = this.responsibilityDirectorySnapshot?.attached_roles_data;
      return Array.isArray(roles) ? roles : [];
    },
    responsibilityDirectoryInitialTabIndex() {
      return this.mapResponsibilityQueryToTabIndex(this.$route.query?.resTab);
    },
    responsibilityDirectoryBadgeHolderFilterLevels() {
      const f = this.$route.query?.filter;
      if (f === "coach") return ["Coach"];
      if (f === "master") return ["Master"];
      return [];
    },
    responsibilityDrilldownPurposeTrimmed() {
      const p = this.responsibilityDrilldownPurpose;
      if (typeof p !== "string") return "";
      return p.trim();
    },
    responsibilityDirectoryPageProps() {
      const row = this.responsibilityDirectorySnapshot;
      const resId = this.directorySelectedResponsibilityId;
      const cp = row?.checkpoint || {};
      const badgeRollupTotal =
        Number(cp.apprentice || 0) +
        Number(cp.professional || 0) +
        Number(cp.coach || 0) +
        Number(cp.master || 0);
      const coachesLen = Array.isArray(row?.coaches_list) ? row.coaches_list.length : 0;
      const mastersLen = Array.isArray(row?.masters_list) ? row.masters_list.length : 0;
      const assignmentsCount = Number(row?.count_assignments ?? 0);
      const checklistCount = this.responsibilityDrilldownChecklistTasks.length;
      const badgeListCount =
        this.responsibilityDrilldownBadgeHoldersCount ||
        (badgeRollupTotal > 0 ? badgeRollupTotal : 0);

      return {
        resId,
        header: this.buildResponsibilityDirectoryHeader(row, resId),
        initialTabIndex: this.responsibilityDirectoryInitialTabIndex,
        menuItems: [
          { title: "Assignments", count: assignmentsCount },
          { title: "Checklist", count: checklistCount },
          { title: "Badge Holders", count: badgeListCount },
          { title: "Coaches", count: coachesLen },
          { title: "Masters", count: mastersLen },
          { title: "Context", count: null },
        ],
        assignmentsTab: {
          responsibilityId: resId,
          accountId: this.accountID,
        },
        checkpointsTable: {
          tasks: this.responsibilityDrilldownChecklistTasks,
          showPagination: this.responsibilityDrilldownChecklistTasks.length > 10,
          pagination: {
            total: this.responsibilityDrilldownChecklistTasks.length,
            page: 1,
            perPage: 10,
          },
        },
        badgeHoldersTab: {
          items: this.responsibilityDrilldownBadgeHolders,
          itemsCount: this.responsibilityDrilldownBadgeHoldersCount,
          responsibilityId: resId,
          itemsPerPage: 10,
          useClientPagination: false,
          filterLevels: this.responsibilityDirectoryBadgeHolderFilterLevels,
          badgeLevelCounts: {
            apprentice: Number(cp.apprentice || 0),
            professional: Number(cp.professional || 0),
            coach: Number(cp.coach || 0),
            master: Number(cp.master || 0),
          },
        },
        coachesInspectors: this.mapDirectoryCoachesToInspectorCards(row?.coaches_list),
        mastersInspectors: this.mapDirectoryMastersToInspectorCards(row?.masters_list),
        showCoachesInspectorsHeader: coachesLen > 0,
        showMastersInspectorsHeader: mastersLen > 0,
      };
    },
  },
  watch: {
    showResponsibilityDirectoryInline(val) {
      if (val && this.directorySelectedResponsibilityId) {
        this.loadResponsibilityDrilldownDetail();
      } else if (!val) {
        this.responsibilityDrilldownChecklistTasks = [];
        this.responsibilityDrilldownBadgeHolders = [];
        this.responsibilityDrilldownBadgeHoldersCount = 0;
        this.responsibilityDrilldownPurpose = "";
      }
    },
    directorySelectedResponsibilityId(newId, oldId) {
      if (this.showResponsibilityDirectoryInline && newId && newId !== oldId) {
        this.loadResponsibilityDrilldownDetail();
      }
    },
  },
  methods: {
    onChangeMenu(index) {
      this.selectedTab = index;
      this.directoryView.resetInlineDirectoryView();
      const selectedTabTitle = this.menuItems[index]?.title;
      if (selectedTabTitle === "Employee Directory") {
        this.getDirectoryEmployees();
      } else if (selectedTabTitle === "Responsibilities Directory") {
        this.getDirectoryResponsibilities();
      }
    },
    onEmployeeDirectoryBreadcrumb() {
      this.directoryView.resetInlineDirectoryView();
    },
    async onResponsibilityDirectoryBreadcrumb() {
      this.directoryView.closeResponsibilityDirectoryInline();
      await this.getDirectoryResponsibilities();
    },
    mapResponsibilityQueryToTabIndex(tab) {
      const TAB_ASSIGNMENTS = 0;
      const TAB_CHECKLIST = 1;
      const TAB_BADGE_HOLDERS = 2;
      const TAB_COACHES = 3;
      const TAB_MASTERS = 4;
      /** Must match `responsibilityDirectoryPageProps.menuItems` length (6 tabs; Context is last index 5). */
      const TAB_CONTEXT = 5;
      const t = String(tab || "assignments").toLowerCase();
      if (t === "checklist") return TAB_CHECKLIST;
      if (t === "badge-holders") return TAB_BADGE_HOLDERS;
      if (t === "coaches") return TAB_COACHES;
      if (t === "masters") return TAB_MASTERS;
      if (t === "context") return TAB_CONTEXT;
      if (t === "assignments") return TAB_ASSIGNMENTS;
      return TAB_ASSIGNMENTS;
    },
    onResponsibilityDirectoryTabChange(index) {
      const names = ["assignments", "checklist", "badge-holders", "coaches", "masters", "context"];
      const resTab = names[index] || "assignments";
      const query = { ...this.$route.query };
      query.resTab = resTab;
      if (resTab !== "badge-holders") {
        delete query.filter;
      }
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },
    buildResponsibilityDirectoryHeader(row, resId) {
      const listHref =
        this.$route.name === "YourStuffDirectories" ? "/your-stuff/directories" : "/directories";
      const codeNum =
        (row && row.code && String(row.code).replace(/^#/, "")) ||
        (resId != null ? String(resId) : "");
      const readiness =
        row && Number(row.count_assignments) > 0
          ? Number(row.avg_readiness ?? row.current_readiness ?? 0)
          : 0;
      return {
        name: (row && row.name) || "Responsibility",
        responsibilityId: codeNum,
        status: "active",
        archived: false,
        readiness,
        lastMonthReadiness: null,
        breadcrumbsList: [
          { text: "Responsibilities Directory", href: listHref },
          { text: "Responsibility", disabled: true },
        ],
      };
    },
    mapDirectoryPeopleToInspectorCards(list, badgeLevel) {
      if (!Array.isArray(list)) return [];
      return list.map((c) => {
        const full = String(c.full_name ?? c.name ?? "").trim();
        const parts = full.split(/\s+/).filter(Boolean);
        return {
          badgeLevel,
          firstName: parts[0] ?? "",
          lastName: parts.slice(1).join(" ") ?? "",
          title: c.title || c.position_name || c.position || "",
          img: c.avatar ?? c.img ?? null,
          isHoverEnabled: true,
        };
      });
    },
    mapDirectoryCoachesToInspectorCards(list) {
      return this.mapDirectoryPeopleToInspectorCards(list, "Coach");
    },
    mapDirectoryMastersToInspectorCards(list) {
      return this.mapDirectoryPeopleToInspectorCards(list, "Master");
    },
    drilldownInspectionLevelName(level) {
      const levelMap = { 1: "Apprentice", 2: "Professional", 3: "Coach", 4: "Master" };
      return levelMap[level] || "Apprentice";
    },
    mapChecklistRowForDirectoryPage(item) {
      const micro = item.microlearning;
      const video =
        micro && typeof micro === "string" && (micro.includes("<iframe") || micro.includes("http"))
          ? micro
          : "";
      return {
        id: item.id,
        checkpoint_id: item.id,
        level: this.drilldownInspectionLevelName(item.proficiency_level),
        level_name: this.drilldownInspectionLevelName(item.proficiency_level),
        proficiency_level: item.proficiency_level,
        criteria: item.criteria,
        instructions: item.instructions,
        video,
        status: item.status,
        evaluation_status: "pending",
        inspectionStatus: "pending-evaluation",
        isPassed: false,
        isFailed: false,
        isNotApplicable: false,
        failed_by: null,
        failed_on: null,
        failed_reason: null,
        first_name: "",
        last_name: "",
        updated_on: null,
        updated_by: null,
        avatar: "",
        not_applicable_reason: "",
        obsolete_reason: "",
        reset_reason: "",
        showAllInstructionIcon: true,
      };
    },
    transformDirectoryDrilldownBadgeHolders(results) {
      if (!results || !Array.isArray(results)) return [];
      return results.map((item) => {
        const fullName = `${item.issued_to?.first_name || ""} ${
          item.issued_to?.last_name || ""
        }`.trim();
        const badgeView =
          (!item.is_current && !!item.cause) || (item.is_current && !item.removed_by)
            ? "Editable"
            : "ReadOnly";
        return {
          id: item.id,
          is_current: item.is_current,
          user: {
            id: item.issued_to?.id,
            name: fullName,
            email: item.issued_to?.email || "",
          },
          employee: {
            id: item.issued_to?.id,
            name: fullName,
            avatar: item.issued_to?.avatar,
            title: item.issued_to?.title || null,
          },
          userStatus: item.issued_to?.current_status || "active",
          employeeStatus: item.issued_to?.current_status || "active",
          accountHover: true,
          badge: {
            level: item.level || "Novice",
            dot: this.getDirectoryBadgeDotFromStatus(item.status),
            view: badgeView,
            expiration_date: item.expiration_date || null,
            has_expiration_if_obsolete:
              !!item.expires_when_obsolete || Number(item.awarded_expiration_obsolete || 0) > 0,
            upgraded: !item.is_current && !!item.cause,
            upgradedVariantIcon: this.getDirectoryBadgeUpgradedVariantIcon(item),
            unread: 0,
            hideChatterIcon: true,
          },
          assignedBy: item.issued_by
            ? {
                name: item.issued_by.name || "",
                picture: item.issued_by.picture || "",
                userType: "user",
                displayDate: item.issued_on || null,
              }
            : null,
          removedBy: item.removed_by
            ? {
                name: item.removed_by.name || "",
                picture: item.removed_by.picture || "",
                userType: "user",
                displayDate: item.removed_on || null,
              }
            : null,
          reason:
            item.remove_reason &&
            typeof item.remove_reason === "string" &&
            item.remove_reason.trim() !== ""
              ? item.remove_reason
              : "",
          status: item.status,
          cause: item.cause,
          issued_for: item.issued_for,
        };
      });
    },
    async loadResponsibilityDrilldownDetail() {
      const resId = this.directorySelectedResponsibilityId;
      const accountId = this.accountID;
      if (!resId || !accountId) return;
      try {
        this.$loader?.show();
        const routeFilter = this.$route.query?.filter;
        const levelParts = [];
        if (routeFilter === "coach") {
          levelParts.push(`level=${encodeURIComponent("Coach")}`);
        } else if (routeFilter === "master") {
          levelParts.push(`level=${encodeURIComponent("Master")}`);
        }
        const badgeQueryParts = [
          `status=${encodeURIComponent("Current")}`,
          `limit=${encodeURIComponent(100)}`,
          `offset=${encodeURIComponent(0)}`,
          ...levelParts,
        ];
        const [checklistRes, badgeRes] = await Promise.all([
          getResponsibilityChecklistV2(accountId, resId, { status: "active" }),
          getListofResponsibilityBadge(accountId, resId, "responsibility", {
            _query: badgeQueryParts.join("&"),
          }),
        ]);

        const checklistData = checklistRes?.data;
        const checklistList = Array.isArray(checklistData)
          ? checklistData
          : Array.isArray(checklistData?.results)
          ? checklistData.results
          : [];
        this.responsibilityDrilldownChecklistTasks = checklistList.map((item) =>
          this.mapChecklistRowForDirectoryPage(item)
        );

        if (badgeRes?.status === 200) {
          const results = badgeRes?.data?.results || [];
          this.responsibilityDrilldownBadgeHolders =
            this.transformDirectoryDrilldownBadgeHolders(results);
          this.responsibilityDrilldownBadgeHoldersCount = badgeRes?.data?.count ?? results.length;
        } else {
          this.responsibilityDrilldownBadgeHolders = [];
          this.responsibilityDrilldownBadgeHoldersCount = 0;
        }

        try {
          const detailRes = await getResponsibilityIdV2(accountId, resId, "responsibility");
          const purposeRaw = detailRes?.data?.purpose;
          this.responsibilityDrilldownPurpose =
            typeof purposeRaw === "string"
              ? purposeRaw
              : purposeRaw != null
              ? String(purposeRaw)
              : "";
        } catch (purposeErr) {
          console.warn("Could not load responsibility purpose:", purposeErr?.message || purposeErr);
          this.responsibilityDrilldownPurpose = "";
        }
      } catch (e) {
        console.error("Responsibility drill-down detail load failed:", e);
        this.responsibilityDrilldownChecklistTasks = [];
        this.responsibilityDrilldownBadgeHolders = [];
        this.responsibilityDrilldownBadgeHoldersCount = 0;
        this.responsibilityDrilldownPurpose = "";
      } finally {
        this.$loader?.hide();
      }
    },
    getDirectoryBadgeDotFromStatus(status) {
      switch (status) {
        case "given":
          return "Given";
        case "obsolete":
          return "Obsolete";
        default:
          return "Good Standing";
      }
    },
    getDirectoryBadgeView(item) {
      return getBadgeViewFromCause(item);
    },
    getDirectoryBadgeUpgradedVariantIcon(item) {
      return getUpgradedVariantIconFromCause(item);
    },
    mapDirectoryBadgeApiRow(item) {
      return {
        id: item.id,
        is_current: item.is_current,
        issued_to: item.issued_to,
        issued_by: item.issued_by,
        issued_on: item.issued_on,
        removed_on: item.removed_on,
        access: item.access,
        currentBadge: {
          level: item.level || "",
          dot: this.getDirectoryBadgeDotFromStatus(item.status),
          view: this.getDirectoryBadgeView(item),
          expiration_date: item.expiration_date,
          has_expiration_if_obsolete: item.expires_when_obsolete,
          is_current: item.is_current,
          upgraded: !item.is_current && !!item.cause,
          upgradedVariantIcon: this.getDirectoryBadgeUpgradedVariantIcon(item),
          withoutFeatureFlag: true,
        },
        responsibility: {
          code: item.issued_for?.code,
          name: item.issued_for?.name,
        },
        removedBy: item.removed_by
          ? {
              name: item.removed_by?.name,
              picture: item.removed_by?.picture || "",
              userType: "user",
              date: item?.removed_on || "",
            }
          : null,
        grantedBy: item.awarded_by
          ? {
              name: item.awarded_by?.name,
              picture: item.awarded_by?.picture || "",
              userType: "user",
              date: item?.awarded_on || "",
            }
          : null,
        reason: item.remove_reason === "string" ? "" : item.remove_reason,
        status: item.status,
        cause: item.cause,
      };
    },
    getInitialAccountDetails() {
      const fromRoot = this.$root?.currentAccountDetails;
      if (fromRoot && typeof fromRoot === "object" && Object.keys(fromRoot).length > 0) {
        return fromRoot;
      }
      try {
        const stored = localStorage.getItem("currentAccountDetails");
        return stored ? JSON.parse(stored) : {};
      } catch {
        return {};
      }
    },
    async getAccountDetails() {
      try {
        this.$loader?.show();
        const response = await getAccountData(this.accountID);
        if (response?.data) {
          this.currentAccountDetails = response.data;
        }
      } catch {
        this.currentAccountDetails = this.getInitialAccountDetails();
      } finally {
        this.$loader?.hide();
      }
    },
    async getDirectoryEmployees() {
      // idea-MDP-560: on the initial load the table skeleton replaces the app-level loader.
      const skeletonReplacesLoader = this.skeletonLoadingEnabled && this.employeesInitialLoading;
      try {
        if (!skeletonReplacesLoader) this.$loader?.show();
        const offset = (this.employeePage - 1) * this.employeeLimit;
        const response = await getDirectoryEmployeesV2(this.accountID, {
          limit: this.employeeLimit,
          offset,
          status: "active",
          name: this.employeeSearchText,
        });
        const data = response?.data?.results ?? response?.data;
        const list = Array.isArray(data) ? data : [];
        this.dataSource = list.length ? this.transformDataV2(list) : [];
        const count = response?.data?.count ?? 0;
        this.employeeTotalCount = count;
        this.activeEmployeeCount = count;
      } catch (error) {
        console.error("Failed to fetch directory employees:", error);
        this.dataSource = [];
        this.employeeTotalCount = 0;
        this.activeEmployeeCount = 0;
      } finally {
        if (!skeletonReplacesLoader) this.$loader?.hide();
      }
    },
    async initSkeletonLoadingFlag() {
      try {
        this.skeletonLoadingEnabled = (await this.$getFlag?.(LD_FLAG_IDEA_MDP_560, false)) === true;
      } catch {
        this.skeletonLoadingEnabled = false;
      }
    },
    async getDirectoryResponsibilities() {
      // idea-MDP-560: the table skeleton is the loading affordance, so the app-level loader is skipped.
      const skeletonReplacesLoader = this.skeletonLoadingEnabled;
      try {
        this.respLoading = true;
        if (!skeletonReplacesLoader) this.$loader?.show();
        const offset = (this.respPage - 1) * this.respLimit;
        const response = await getDirectoryResponsibilitiesV2(this.accountID, {
          limit: this.respLimit,
          offset,
          status: "active",
          name: this.respSearchText,
        });
        const data = response?.data?.results ?? response?.data;
        const list = Array.isArray(data) ? data : [];
        this.respDataSource = list.length ? this.transformRespDataV2(list) : [];
        const count = response?.data?.count ?? 0;
        this.respTotalCount = count;
        this.activeResponsibilityCount = count;
      } catch (error) {
        console.error("Failed to fetch directory responsibilities:", error);
        this.respDataSource = [];
        this.respTotalCount = 0;
        this.activeResponsibilityCount = 0;
      } finally {
        this.respLoading = false;
        if (!skeletonReplacesLoader) this.$loader?.hide();
      }
    },
    onEmployeePageRequest({ page, itemsPerPage }) {
      this.employeePage = page;
      this.employeeLimit = itemsPerPage;
      this.getDirectoryEmployees();
    },
    onEmployeeSearch(text) {
      this.employeeSearchText = text || "";
      this.employeePage = 1;
      this.getDirectoryEmployees();
    },
    onRespPageRequest({ page, itemsPerPage }) {
      this.respPage = page;
      this.respLimit = itemsPerPage;
      this.getDirectoryResponsibilities();
    },
    onRespSearch(text) {
      this.respSearchText = text || "";
      this.respPage = 1;
      this.getDirectoryResponsibilities();
    },
    transformDataV2(dataArray) {
      return dataArray.map((data) => {
        const user = data.user ?? data;
        const primaryPositions = data.primary_positions ?? [];
        const positionName = primaryPositions.length > 0 ? primaryPositions[0].name : "";
        return {
          id: data.id,
          first_name: `${user.first_name ?? data.first_name ?? ""}`,
          last_name: `${user.last_name ?? data.last_name ?? ""}`,
          position: positionName,
          checkpoint: data.badge_counts ?? data.checkpoint ?? {},
          avatar: data.avatar ?? user.avatar,
          current_readiness: data.current_readiness ?? 0,
          current_status: data.current_status ?? data.status ?? "active",
          badges_count: data.badges_count ?? 0,
        };
      });
    },
    transformRespDataV2(dataArray) {
      return dataArray.map((data) => {
        const masters = data.masters ?? [];
        const coachList = data.coach ?? [];
        const apprenticeList = data.apprentice ?? [];
        const professionalList = data.professional ?? [];
        const masterFullNames = masters.map((item) => item.full_name);

        const coach_badges = coachList
          .filter((item) => !masterFullNames.includes(item.full_name))
          .map((item) => {
            const parts = (item.full_name ?? "").split(" ");
            return {
              img: item.avatar,
              fullName: item.full_name,
              firstName: parts[0] ?? "",
              lastName: parts.slice(1).join(" ") ?? "",
              badge_level: "coach",
            };
          });

        const master_badges = masters.map((item) => {
          const parts = (item.full_name ?? "").split(" ");
          return {
            img: item.avatar,
            fullName: item.full_name,
            firstName: parts[0] ?? "",
            lastName: parts.slice(1).join(" ") ?? "",
            badge_level: "master",
          };
        });

        const apprentice_badges = apprenticeList.map((item) => {
          const parts = (item.full_name ?? "").split(" ");
          return {
            img: item.avatar,
            full_name: item.full_name,
            firstName: parts[0] ?? "",
            lastName: parts.slice(1).join(" ") ?? "",
            badge_level: "apprentice",
          };
        });

        const professional_badges = professionalList.map((item) => {
          const parts = (item.full_name ?? "").split(" ");
          return {
            img: item.avatar,
            full_name: item.full_name,
            firstName: parts[0] ?? "",
            lastName: parts.slice(1).join(" ") ?? "",
            badge_level: "professional",
          };
        });

        return {
          id: data.id,
          name: data.name ?? "",
          code: this.extractCode(data.code_str ?? data.code),
          code_str: data.code_str ?? "",
          current_readiness:
            data.avg_readiness ?? data.current_readiness ?? data.average_readiness ?? 0,
          avg_readiness: data.avg_readiness ?? 0,
          count_assignments: data.count_assignments ?? 0,
          attached_roles_data: data.attached_roles_data ?? [],
          attached_role_count: data.attached_role_count ?? 0,
          coaches_list: data.coaches ?? [],
          masters_list: data.masters ?? [],
          coach_badges,
          master_badges,
          apprentice_badges,
          professional_badges,
          checkpoint: {
            apprentice: data.badge_apprentice_holder_count ?? data.checkpoint?.apprentice ?? 0,
            professional:
              data.badge_proficient_holder_count ??
              data.badge_professional_holder_count ??
              data.checkpoint?.professional ??
              0,
            master:
              data.badge_architect_holder_count ??
              data.badge_master_holder_count ??
              data.checkpoint?.master ??
              0,
            coach: data.badge_coach_holder_count ?? data.checkpoint?.coach ?? 0,
          },
          all_badges: [
            ...coach_badges,
            ...master_badges,
            ...apprentice_badges,
            ...professional_badges,
          ],
        };
      });
    },
    transformData(dataArray) {
      return dataArray.map((data) => ({
        id: data.id,
        first_name: `${data.first_name}`,
        last_name: `${data.last_name}`,
        position: data?.primary_positions.length > 0 ? data.primary_positions[0].name : "",
        checkpoint: data.badge_counts,
        avatar: data.avatar,
        current_readiness: data.current_readiness,
        current_status: data.current_status,
      }));
    },
    transformRespData(dataArray) {
      return dataArray.map((data) => {
        const masterFullNames = data.masters.map((item) => item.full_name);

        const coach_badges = data.coach
          .filter((item) => !masterFullNames.includes(item.full_name))
          .map((item) => {
            const [firstName, lastName] = item.full_name.split(" ");
            return {
              img: item.avatar,
              fullName: item.full_name,
              firstName,
              lastName,
              badge_level: "coach",
            };
          });

        const master_badges = data.masters.map((item) => {
          const [firstName, lastName] = item.full_name.split(" ");
          return {
            img: item.avatar,
            fullName: item.full_name,
            firstName,
            lastName,
            badge_level: "master",
          };
        });

        const apprentice_badges = data.apprentice.map((item) => {
          const [firstName, lastName] = item.full_name.split(" ");
          return {
            img: item.avatar,
            full_name: item.full_name,
            firstName,
            lastName,
            badge_level: "apprentice",
          };
        });

        const professional_badges = data.professional.map((item) => {
          const [firstName, lastName] = item.full_name.split(" ");
          return {
            img: item.avatar,
            full_name: item.full_name,
            firstName,
            lastName,
            badge_level: "professional",
          };
        });

        return {
          id: data.id,
          name: data.name,
          code: this.extractCode(data.code_str),
          current_readiness: data.current_readiness ?? data.average_readiness ?? 0,
          coach_badges,
          master_badges,
          apprentice_badges,
          professional_badges,
          checkpoint: {
            apprentice: data.badge_apprentice_holder_count,
            professional:
              data.badge_proficient_holder_count ?? data.badge_professional_holder_count,
            master: data.badge_architect_holder_count ?? data.badge_master_holder_count,
            coach: data.badge_coach_holder_count,
          },
          all_badges: [
            ...coach_badges,
            ...master_badges,
            ...apprentice_badges,
            ...professional_badges,
          ],
        };
      });
    },
    extractCode(str) {
      let match = str?.match(/#\d+/);
      return match ? match[0] : null;
    },
  },
};
</script>

<style lang="css" scoped>
.readiness-container {
  width: 100%;
}

.page-title {
  margin-top: 16px;
}

.employee-path-to-readiness {
  /* box-shadow: 0px 1px 2px 0px #1018280d; */
  border-radius: 8px;
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px;
}
.employee-readiness-title {
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  color: #344054;
  min-width: 125px;
}
.employee-readiness {
  width: 100%;
  margin-left: 15px;
}
.employee-readiness .progress-bar-container {
  width: 100%;
}
.employee-readiness .progress-value {
  font-weight: 600;
}
.employee-path {
  border-radius: 8px;
}
::v-deep .progress-bar-container {
  width: 100%;
}
.employee-readiness-container {
  width: 100%;
}

#treelist {
  max-width: 100%;
}

::v-deep(.employee-readiness-menu-container .v-tabs) {
  padding: 13px 0px !important;
  border-bottom: 1px solid #eaecf0 !important;
}

::v-deep(.custom-tabs) {
  top: 20px;
}
.directories-container {
  width: 98%;
  border: 1px solid #e5e5e5;
  border-radius: 8px !important;
  padding: 12px;
  margin: 12px 24px 12px 12px;
}

.directories-view__attached-roles {
  padding: 8px 24px 24px;
  box-sizing: border-box;
}

.directories-view__attached-role-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #eaecf0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  color: #101828;
}

.directories-view__attached-role-code {
  color: #667085;
  font-size: 13px;
}

.directories-view__attached-roles--empty {
  color: #667085;
  font-size: 14px;
}

.directories-view__context-purpose {
  padding: 8px 24px 24px;
  box-sizing: border-box;
  max-width: 100%;
}

.directories-view__context-purpose-text {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 24px;
  color: #101828;
  white-space: pre-wrap;
}

.directories-view__context-purpose-empty {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 22px;
  color: #667085;
}
</style>
